import { useCallback, useRef, useState } from "react"

import { ListView } from "./list"
import { EmptyView } from "./views"
import { PlusButton } from "./buttons"

import { ReactComponent as CaretIcon } from "../assets/icons/button/caret_icon.svg"
import { ReactComponent as CloseIcon } from "../assets/icons/button/close_icon.svg"
import { ReactComponent as AlertIcon } from "../assets/icons/alert_icon.svg"

export const CollapsableContainer = ({
  defaultOpenState = false,
  collapsible = true,
  onClose,
  ...props
}) => {
  const { className, children, header, ...rest } = props
  const [isOpen, setOpen] = useState(() => defaultOpenState)

  const handleHeaderClick = useCallback(() => {
    if (collapsible && !onClose) {
      setOpen(old => !old)
    }
  }, [collapsible, onClose])

  const handleCloseClick = useCallback(
    e => {
      e.stopPropagation()
      if (onClose) {
        onClose()
      }
    },
    [onClose]
  )

  let headerView = null,
    contentView = null

  // Use header prop if provided, otherwise fall back to children-based approach
  if (header) {
    headerView = header
    contentView = children
  } else if (children) {
    if (!Array.isArray(children)) {
      contentView = children
    } else if (children.length === 1) {
      contentView = children[0]
    } else {
      headerView = children[0]
      contentView = children.slice(1)
    }
  }

  // Determine behavior based on props
  const hasOnClose = !!onClose
  const isClosable = hasOnClose
  const isCollapsible = collapsible && !hasOnClose

  // When closable, always show content; otherwise respect collapsible behavior
  const isContentVisible = isClosable ? true : collapsible ? isOpen : true

  // Header only clickable if collapsible (not closable or non-collapsible)
  const headerClassName = `collapsable-container__header${isCollapsible ? " collapsable-container__header--clickable" : ""}`

  // Determine which icon to show
  const showCloseIcon = isClosable
  const showCaretIcon = isCollapsible

  return (
    <div
      className={`collapsable-container ${className || ""}`}
      {...rest}
      aria-expanded={isContentVisible}
    >
      <div className={headerClassName} onClick={handleHeaderClick}>
        <div>{headerView}</div>
        {showCaretIcon && (
          <div className="caret-icon">
            <CaretIcon/>
          </div>
        )}
        {showCloseIcon && (
          <div className="close-icon" onClick={handleCloseClick}>
            <CloseIcon/>
          </div>
        )}
      </div>
      <div className="collapsable-container__body">{contentView}</div>
    </div>
  )
}

export const CollapsableListView = ({
  hasData,
  isLoading,
  datasource,
  delegate,
  scrollable = true
}) => {
  if (!hasData) {
    return <EmptyView>{isLoading ? "Loading..." : "No Resources"}</EmptyView>
  }

  const children = []
  for (let row = 0; row < datasource.numberOfRows(); row++) {
    const rowDatasource = datasource.getDataForRow(row)

    children.push(
      <CollapsableView
        key={rowDatasource.resource.id}
        datasource={rowDatasource}
        delegate={delegate}
      />
    )
  }

  const content = <div className="map-listview-collapsable">{children}</div>

  if (scrollable) {
    return (
      <ListView
        className="map-listview-content"
        scrollableClassName="map-listview-content-scrollable"
      >
        {content}
      </ListView>
    )
  }

  return content
}

const CollapsableView = ({ datasource, delegate }) => {
  const caretRef = useRef(null)

  if (datasource?.isVisible != null && !datasource.isVisible()) {
    return
  }

  const handleOnResourceSelected = e => {
    if (
      !datasource.isSelectable() ||
      (datasource.isSelected() && caretRef.current && caretRef.current.contains(e.target))
    ) {
      return
    }

    delegate?.onSelect && delegate.onSelect(datasource.isSelected() ? null : datasource.resource)
  }

  const handleOnAdd = () => {
    delegate?.onCreate && delegate.onCreate(datasource.resource)
  }

  const caretOrPlaceholder = datasource.hasSubResources() ? (
    <div
      className="map-listview-collapsable-layout-caret"
      ref={caretRef}
      onClick={datasource.handleToggle}
      aria-expanded={datasource.isOpen()}
    >
      <div>
        <CaretIcon/>
      </div>
    </div>
  ) : (
    <span/>
  )

  return (
    <div id={datasource.resource.id} className="map-listview-collapsable">
      <div
        className="map-listview-collapsable-layout"
        aria-multiline={datasource.hasSubResources()}
      >
        {caretOrPlaceholder}
        <div
          className={`map-listview-collapsable-header ${datasource.isSelectable() ? "selectable" : ""}`}
          aria-selected={datasource.isSelected()}
        >
          <div
            className={`map-listview-collapsable-header-content ${datasource.hasIcon() ? "three-column" : "two-column"}`}
          >
            {datasource.hasIcon()
              ? datasource.getIcon(datasource.resource, handleOnResourceSelected)
              : null}
            <span className="map-listview-collapsable-header-content-primary">
              {datasource.resource.name}
            </span>
            <div className="map-listview-collapsable-header-content-buttons">
              {!datasource.resource.isComplete() &&
                <AlertIcon className="red-color"/>}
              {datasource.isCreatable() && <PlusButton onClick={handleOnAdd}/>}
            </div>
            <span className="map-listview-collapsable-header-content-secondary">
              {datasource.resource.description}
            </span>
            <div
              className="map-listview-collapsable-header-click"
              onClick={handleOnResourceSelected}
            />
          </div>
        </div>
        <div className="map-listview-collapsable-content">
          {datasource.hasSubResources() &&
            datasource.isOpen() &&
            datasource
              .subResourceDatasources()
              .map(subResourceDatasource => (
                <CollapsableView
                  key={subResourceDatasource.resource.id}
                  datasource={subResourceDatasource}
                  delegate={delegate}
                />
              ))}
        </div>
      </div>
    </div>
  )
}
